<template>
	<span class="flex text-5xl md:text-7xl w-full justify-center text-center py-8">Gallery</span>
	<div class="flex w-full items-center justify-center">
		<Divider class="w-32! mt-0!" />
	</div>
	<div class="grid grid-cols-(--grid-cols) md:auto-rows-2.5 justify-center w-dvw min-h-(--min-height-custom-mobile) lg:min-h-(--min-height-custom) py-4 gap-4">
		<!-- Fullscreen image gallery component -->
		<Galleria
			v-model:visible="isPopupActive"
			:full-screen="true"
			:value="photoPagePhotos"
			:show-indicators="false"
			:show-thumbnails="false"
			:num-visible="1"
			:active-index="currentPhotoIndex"
			show-item-navigators
			circular
			container-class="!rounded-lg"
		>
			<!-- Slot template for rendering each gallery item -->
			<template #item="slotProps">
				<div class="flex flex-col items-center">
					<div class="flex flex-col items-center max-h-(--max-img-height) rounded-lg">
						<!-- Displays the image using Nuxt's ImageKit provider -->
						<NuxtImg
							v-if="ALLOW_API_CALLS"
							class="object-contain max-h-(--max-img-height) rounded-lg"
							provider="imagekit"
							:src="slotProps.item.src"
							:alt="slotProps.item.alt"
							@error="onImageError"
						/>
						<img
							v-else
							class="object-contain max-h-(--max-img-height) rounded-lg"
							:src="slotProps.item.src"
							:alt="slotProps.item.alt"
							@error="onImageError"
						>
					</div>
				</div>
			</template>

			<!-- Slot template for rendering the image caption -->
			<template #caption="slotProps">
				<p class="text-white">
					{{ slotProps.item.caption }}
				</p>
			</template>
		</Galleria>

		<!-- Grid of images displayed as thumbnails -->
		<div
			v-for="photo in photoPagePhotos"
			:key="photo.index"
			class="grid-item justify-center flex max-w-80 max-h-120 overflow-scroll group transition duration-(--default-transition-duration) hover:cursor-pointer hover:scale-102"
		>
			<div
				v-if="!contentLoaded"
				class="size-full"
			>
				<LoadingCard
					:show-loading-text="false"
					height="full"
				/>
			</div>
			<div v-else>
				<!-- Displays each image thumbnail using Nuxt's ImageKit provider -->
				<NuxtImg
					v-if="ALLOW_API_CALLS"
					class="object-cover max-h-(--max-img-height) rounded-lg group-hover:shadow-lg"
					provider="imagekit"
					:src="photo.src"
					:alt="photo.alt"
					:loading="photo.index < EAGER_LOAD_COUNT ? 'eager' : 'lazy'"
					@click="togglePopup(photo.index)"
					@load="onImageLoad"
					@error="onImageError"
				/>
				<img
					v-else
					class="object-contain max-h-(--max-img-height) rounded-lg"
					:src="photo.src"
					:alt="photo.alt"
					:loading="photo.index < EAGER_LOAD_COUNT ? 'eager' : 'lazy'"
					@click="togglePopup(photo.index)"
					@load="onImageLoad"
					@error="onImageError"
				>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import LoadingCard from "~/components/LoadingCard.vue";
	import {
		Divider,
		Galleria
	} from "primevue";

	const currentPhotoIndex = ref<number>( 1 );
	const isPopupActive = ref<boolean>( false );
	const { contentLoaded, photoPagePhotos } = imageDataStores();
	const { ALLOW_API_CALLS } = globalStores();
	const { showToastMessage } = useUtils();

	/**
	 * Toggles the visibility of the popup and sets the current photo index.
	 *
	 * @param {number} index - The index of the photo to display when the popup is active.
	 */
	const togglePopup = ( index: number ): void => {
		isPopupActive.value = !isPopupActive.value;
		currentPhotoIndex.value = isPopupActive.value ? index : 0;
	};

	const ROW_HEIGHT = 10; // same as auto-rows
	const GAP = 16; // same as Tailwind gap-4 in px
	const EAGER_LOAD_COUNT = 16; // roughly covers the first couple rows on common breakpoints; the rest load lazily

	/**
	 * Sets the grid-row-end span of a single grid item based on the
	 * current rendered height of its image.
	 *
	 * @param {HTMLElement} item - The `.grid-item` element to size.
	 */
	const setGridItemSpan = ( item: HTMLElement ) => {
		const img = item.querySelector<HTMLImageElement>( "img" );
		if ( !img ) return;
		const totalHeight = img.getBoundingClientRect().height + GAP;
		const rowSpan = Math.ceil( totalHeight / ( ROW_HEIGHT + GAP ) );
		item.style.gridRowEnd = `span ${ rowSpan }`;
	};

	const setGridItemSpans = ( container: HTMLElement ) => {
		const items = container.querySelectorAll<HTMLElement>( ".grid-item" );
		items.forEach( setGridItemSpan );
	};

	let resizeObserver: ResizeObserver | null = null;

	/**
	 * (Re)observes every thumbnail image in the grid so that spans get
	 * recalculated whenever an image's rendered size changes - this
	 * catches layout shifts (e.g. responsive breakpoints) that a one-off
	 * `load` event wouldn't.
	 *
	 * @param {HTMLElement} container - The grid container element.
	 */
	const observeGridImages = ( container: HTMLElement ) => {
		resizeObserver?.disconnect();
		resizeObserver = new ResizeObserver( ( entries ) => {
			entries.forEach( ( entry ) => {
				const item = ( entry.target as HTMLElement ).closest<HTMLElement>( ".grid-item" );
				if ( item ) setGridItemSpan( item );
			} );
		} );
		container.querySelectorAll<HTMLImageElement>( ".grid-item img" ).forEach( ( img ) => {
			resizeObserver?.observe( img );
		} );
	};

	/**
	 * Recalculates the span of the grid item belonging to whichever
	 * image just finished loading, since images from ImageKit resolve
	 * at different times and the initial layout pass can't wait on all
	 * of them.
	 *
	 * @param {Event} event - The native `load` event from the `<img>`.
	 */
	const onImageLoad = ( event: Event ) => {
		const img = event.target as HTMLImageElement;
		const item = img.closest<HTMLElement>( ".grid-item" );
		if ( item ) setGridItemSpan( item );
	};

	const applyGridItemSpans = async () => {
		await nextTick();
		const container = document.querySelector<HTMLElement>( ".grid" );
		if ( container ) {
			setGridItemSpans( container );
			observeGridImages( container );
		}
	};

	/**
	 * Notifies the user via toast when a photo fails to load.
	 *
	 * @param {string | Event} payload - The `error` payload from `NuxtImg`
	 * (a src string) or the native `error` event from a plain `<img>`.
	 */
	const onImageError = ( payload: string | Event ): void => {
		const src = typeof payload === "string" ? payload : ( payload.target as HTMLImageElement )?.src;
		showToastMessage( {
			severity: "error",
			summary: "Image failed to load",
			detail: ( typeof payload === "string" ? payload : JSON.stringify( payload ) ) || "One of the photos couldn't be loaded.",
			closable: false
		} );
		console.error( JSON.stringify( payload ) );
	};

	watch( photoPagePhotos, applyGridItemSpans );

	onMounted( applyGridItemSpans );

	onBeforeUnmount( () => {
		resizeObserver?.disconnect();
	} );
</script>

<style scoped>

</style>